import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { ASSETS, OLD_DOMAIN, NEW_DOMAIN } from "../config";

export const BrandEvolution = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const oldY = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const newY = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section
      id="evolution"
      ref={ref}
      className="relative border-b bp-border"
      data-testid="evolution-section"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b bp-border py-6">
          <div>
            <p className="font-mono-x text-[10px] uppercase tracking-[0.3em] text-muted-x">
              // {t.evolution.label}
            </p>
            <h2 className="mt-2 font-display text-3xl uppercase tracking-tight sm:text-4xl lg:text-5xl">
              {t.evolution.title}
            </h2>
          </div>
          <p className="max-w-xs font-mono-x text-xs text-muted-x">
            {t.evolution.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* OLD */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="border-b bp-border p-6 md:border-b-0 md:border-r md:p-10"
            data-testid="evolution-old"
          >
            <div className="mb-5 flex items-center justify-between">
              <span className="font-mono-x text-xs uppercase tracking-[0.2em] text-muted-x">
                [ LEGACY_SYSTEM : v1.0 ]
              </span>
              <span className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-muted-x">
                {t.evolution.oldLabel}
              </span>
            </div>
            <motion.div
              style={{ y: oldY }}
              className="relative border bp-border grayscale-[0.5]"
            >
              <img
                src={ASSETS.oldSiteShot}
                alt="XPDC legacy website"
                loading="lazy"
                className="block w-full"
              />
            </motion.div>
            <p className="mt-4 font-mono-x text-xs text-muted-x">
              {t.evolution.oldDomain}:{" "}
              <span className="line-through">{OLD_DOMAIN}</span>
            </p>
          </motion.div>

          {/* NEW */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="p-6 md:p-10"
            data-testid="evolution-new"
            style={{ background: "var(--xp-surface)" }}
          >
            <div className="mb-5 flex items-center justify-between">
              <span
                className="font-mono-x text-xs uppercase tracking-[0.2em]"
                style={{ color: "var(--xp-primary)" }}
              >
                [ NEW_PLATFORM : v2.0 ]
              </span>
              <span
                className="px-2 py-1 font-mono-x text-[10px] uppercase tracking-[0.15em] text-white"
                style={{ background: "var(--xp-primary)" }}
              >
                {t.evolution.newLabel}
              </span>
            </div>
            <motion.div
              style={{ y: newY, borderColor: "var(--xp-primary)" }}
              className="relative border-2"
            >
              <div
                className="absolute inset-0 -z-10 translate-x-2 translate-y-2"
                style={{ border: "1px solid var(--xp-accent)" }}
              />
              <img
                src={ASSETS.newSiteShot}
                alt="XPDC new website"
                loading="lazy"
                className="block w-full"
              />
            </motion.div>
            <p className="mt-4 font-mono-x text-xs text-muted-x">
              {t.evolution.newDomain}:{" "}
              <span className="font-bold" style={{ color: "var(--xp-primary)" }}>
                {NEW_DOMAIN}
              </span>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
